export type CategoryType = 'Доход' | 'Расход';

export interface Settings {
  savings_target: number;
  currency: string;
  daily_budget_override: number | null;
  /** Операции до этой даты не входят в статистику дашборда (история видна целиком). */
  stats_since: string | null;
  /** Цель месяца: сумма, которая должна уцелеть к концу месяца (защищается в динамическом лимите). */
  month_target: number | null;
  /** Прошёл ли пользователь Сверку планов; '1' после финала. */
  categories_reviewed: string | null;
}

export type SettingKey =
  | 'savings_target'
  | 'currency'
  | 'daily_budget_override'
  | 'stats_since'
  | 'month_target'
  | 'categories_reviewed';

export interface Category {
  id: number;
  name: string;
  type: CategoryType;
  plan_month: number;
  icon: string;
  color: string;
  sort_order: number;
  is_hot: number;
  /** Входит в дневной лимит (повседневные траты). */
  is_daily: number;
  /** Группа для визуализации (напр. «🍽 Еда»); пустая строка — без группы. */
  grp: string;
}

export type GoalTier = 'foundation' | 'need' | 'dream';

export interface Goal {
  id: number;
  name: string;
  need: number;
  have: number;
  remaining: number;
  progress: number;
  deadline: string | null;
  priority: number | null;
  tier: GoalTier;
  /** «Зачем» — одно предложение себе (WOOP). */
  why: string;
  /** Что помешает (обычно — категория-перерасходчик). */
  obstacle: string;
  /** Плановый ежемесячный взнос (для прогноза даты). */
  monthly_plan: number;
  is_shared: number;
  has_image: number;
}

export interface Operation {
  id: string;
  date: string;
  category: string;
  type: string;
  amount: number;
  who: string;
  comment: string;
  created_at: string;
  /** 0/1 — «разовая» трата вне дневного темпа (старые снапшоты поля не имеют). */
  is_one_off?: number;
  /** Пользователь, создавший операцию (старые снапшоты поля не имеют). */
  author_id?: string | null;
}

export type GameEventType =
  | 'chest_open'
  | 'freeze_purchase'
  | 'freeze_use'
  | 'season_close'
  /** Остаток прошлого месяца распределён по целям (ролловер 1-го числа). */
  | 'month_distributed'
  /** Ежедневный вход — детерминированная награда за открытие приложения. */
  | 'daily_login'
  /** Шаг онбординга пройден. */
  | 'onboarding_step'
  /** Онбординг завершён. */
  | 'onboarding_done';

export interface GameEvent {
  id: string;
  type: GameEventType;
  /** Сезон YYYY-MM, к которому относятся монеты. */
  month: string;
  /** Понедельник недели (chest) или дата YYYY-MM-DD (freeze_use, daily_login). */
  week: string;
  coins: number;
  payload: string;
  created_at: string;
  /** Пользователь, создавший событие (старые снапшоты поля не имеют). */
  author_id?: string | null;
}

export interface AddGameEventInput {
  id: string;
  type: GameEventType;
  month: string;
  week?: string;
  coins?: number;
  payload?: string;
}

/** Heartbeat канала импорта: когда источник в последний раз давал сигнал. */
export interface ImportSource {
  source: string;
  last_seen_at: string;
  last_action: 'imported' | 'pending' | 'skipped' | 'duplicate' | 'ignored' | null;
}

/** Доход/расход за месяц (YYYY-MM), целые рубли. Зеркало серверного типа. */
export interface MonthTotal {
  month: string;
  income: number;
  expense: number;
}

/**
 * Серверные агрегаты по всей истории операций — зеркало `functions/_lib/aggregates.ts`
 * (типы в проекте дублируются вручную, DATA_MODEL.md §7).
 */
export interface Aggregates {
  /** Дни (YYYY-MM-DD по МСК), в которые велись записи; не-imp_*, по возрастанию. */
  recorded_days: string[];
  /** Минимальная operations.date по всей таблице (включая imp_*). null — операций нет. */
  first_op_date: string | null;
  /** Минимальный created_at среди не-imp_* операций. null — таких нет. */
  first_op_created_at: string | null;
  /** Доход/расход по месяцам за всю историю, по возрастанию месяца. */
  monthly_totals: MonthTotal[];
  /** Уникальные YYYY-MM из operations.date, по убыванию — для пикера History. */
  op_months: string[];
}

export interface BootstrapData {
  settings: Settings;
  categories: Category[];
  goals: Goal[];
  /** Неразобранные pending_imports — бейдж Inbox честен офлайн (старые снапшоты поля не имеют). */
  pending_count?: number;
  /** Судьба резервов текущего месяца (старые снапшоты поля не имеют). */
  reserve_state?: ReserveStateRow[];
  operations: Operation[];
  game_events: GameEvent[];
  /** Heartbeat каналов импорта (старые снапшоты поля не имеют — до миграции 0014). */
  import_sources?: ImportSource[];
  /**
   * Серверные агрегаты (план 3a). ОПЦИОНАЛЬНО: снапшот в IndexedDB переживает
   * деплой, и первый рендер после обновления идёт из старого снапшота без этого
   * поля. Все потребители обязаны иметь фоллбэк на расчёт по operations.
   */
  aggregates?: Aggregates;
}

export interface PendingImport {
  id: string;
  raw_text: string;
  source: string;
  parsed_type: string | null;
  merchant: string | null;
  amount: number | null;
  mcc: string | null;
  suggested_category: string | null;
  created_at: string;
}

export interface AcceptPendingInput {
  category: string;
  amount?: number;
  comment?: string;
  date?: string;
  remember_rule?: boolean;
}

export interface AddTxInput {
  id: string;
  date: string;
  category: string;
  amount: number;
  who?: string;
  comment?: string;
}

export interface UpdateTxInput {
  id: string;
  date?: string;
  category?: string;
  amount?: number;
  who?: string;
  comment?: string;
  is_one_off?: boolean;
}

export interface ReserveStateRow {
  month: string;
  category: string;
  state: 'released' | 'swept';
}

export interface PutReserveInput {
  month: string;
  category: string;
  /** null — снять отметку released. */
  state: 'released' | 'swept' | null;
}

export interface SweepReserveInput {
  /** Клиентский UUID — идемпотентность ретраев очереди. */
  id: string;
  month: string;
  category: string;
  goal: string;
  amount: number;
}

export interface UpdateGoalInput {
  name: string;
  need?: number;
  have?: number;
  deadline?: string | null;
  priority?: number;
  tier?: GoalTier;
  why?: string;
  obstacle?: string;
  monthly_plan?: number;
  is_shared?: boolean;
}

export interface CreateGoalInput {
  name: string;
  need: number;
  have?: number;
  deadline?: string | null;
  priority?: number;
  tier?: GoalTier;
  why?: string;
  obstacle?: string;
  monthly_plan?: number;
  is_shared?: boolean;
}

export interface UpdateCategoryInput {
  name: string;
  newName?: string;
  plan_month?: number;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_hot?: boolean;
  is_daily?: boolean;
  grp?: string;
}

export interface CreateCategoryInput {
  name: string;
  type: CategoryType;
  plan_month?: number;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_hot?: boolean;
  is_daily?: boolean;
  grp?: string;
}

export interface PushEventsSettings {
  checkin: boolean;
  checkin_time: string; // 'HH:MM', шаг 15 минут
  chest_ready: boolean;
  quest_last_day: boolean;
  season_finale: boolean;
  partner_day: boolean;
  partner_chest: boolean;
  goal_milestone: boolean;
}

export interface SavePushSubscriptionInput {
  endpoint: string;
  p256dh: string;
  auth: string;
  label?: string;
  events?: PushEventsSettings;
}

/**
 * Вид приглашения: `new_household` заводит гостю отдельный бюджет,
 * `join` — пускает в household приглашающего (берётся из сессии, не из тела).
 */
export type InviteKind = 'new_household' | 'join';

/** Строка списка приглашений. Токена здесь нет и быть не может — в БД только хэш. */
export interface Invite {
  /** Первые символы токена — по ним же идёт DELETE. */
  id_prefix: string;
  email: string | null;
  kind: InviteKind;
  created_at: string;
  expires_at: string;
  used_at: string | null;
}

export interface CreateInviteInput {
  kind: InviteKind;
  /** Привязать ссылку к конкретному email; пусто — подойдёт любой. */
  email?: string;
  ttl_hours?: number;
}

/** Ответ на создание: `token` приходит ОДИН раз, дальше его негде взять. */
export interface CreatedInvite {
  token: string;
  expires_at: string;
}

export type ApiErrorCode =
  | 'bad_request'
  | 'not_found'
  | 'conflict'
  | 'internal'
  | 'network'
  /** Нет живой сессии: 401/403 или redirect на HTML-логин Access. */
  | 'unauthorized'
  /** Email не в allowlist семьи (регистрация запрещена). */
  | 'forbidden'
  /** Приглашение не найдено, уже погашено или истекло. */
  | 'invalid_invite'
  /** Слишком много попыток логина — подождать. */
  | 'rate_limited';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export class ApiError extends Error {
  code: ApiErrorCode;
  constructor(code: ApiErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'ApiError';
  }
}
